import * as R from 'ramda';
// constants
import * as GC from '../constants';
// utilities
import endpointsMap from '../utilities/endpoints';
import { sendRequest, sendRequestWithQSParamsSerializer } from '../utilities/http';
// helpers
import { mapConfigValuesByName } from './';
import { getPropFromObject } from './getter';
import { handleException, isResponseSuccess } from './ajax';
import { isNilOrEmpty, isNotNilAndNotEmpty } from './helpers';
import { getAmousCurrentBranchGuidFromWindow } from './window';
import { getImageSrcFromArrayBufferResponse, openFileInWindowFromArrayBufferResponse } from './file';
//////////////////////////////////////////////////

const asyncGetAccessorialsList = async (branchGuid, types) => {
  try {
    const options = {
      params: types ? { types, enterpriseGuid: branchGuid } : { [GC.FIELD_BRANCH_GUID]: branchGuid },
    };

    const endpoint = types ? endpointsMap.accessorialsListByTypes : endpointsMap.accessorialsListEndpoint;

    const { data, status } = await sendRequest('get', endpoint, options);

    if (isResponseSuccess(status)) {
      return R.or(data, []);
    }

    return [];
  } catch (error) {
    handleException('error', 'asyncGetAccessorialsList exception');
  }
};

const asyncGetVinLookupConfig = async (branchGuid) => {
  try {
    if (isNilOrEmpty(branchGuid)) return null;

    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };

    const { data, status } = await sendRequest('get', endpointsMap.fleetVinLookupConfig, options);

    if (isResponseSuccess(status)) {
      return data;
    }

    return null;
  } catch (error) {
    handleException('error', 'asyncGetVinLookupConfig exception');
  }
};

const asyncGetConfigsByNames = async (props) => {
  try {
    const names = getPropFromObject('names', props);
    const branchGuid = R.pathOr(getAmousCurrentBranchGuidFromWindow(), ['branchGuid'], props);

    const params = { names: R.join(',', [names]), [GC.FIELD_BRANCH_GUID]: branchGuid };

    const { data, status } = await sendRequest('get', endpointsMap.branchConfigsEndpoint, { params });

    if (isResponseSuccess(status)) {
      return mapConfigValuesByName(data);
    }

    return null;
  } catch (error) {
    handleException('error', 'asyncGetConfigsByNames exception');
  }
};

const asyncGetFileAndCreateSrc = async (endpoint) => {
  try {
    const options = {
      resType: 'arraybuffer',
    };
    const res = await sendRequestWithQSParamsSerializer('get', endpoint, options);

    const { data, status } = res;

    if (R.and(isNotNilAndNotEmpty(data), isResponseSuccess(status))) {
      const src = getImageSrcFromArrayBufferResponse(res);

      return src;
    }

    return null;
  } catch (error) {
    handleException('error', 'asyncGetFileAndCreateSrc exception');
  }
};

const asyncGetFileAndOpenInWindow = async (endpoint) => {
  try {
    const options = {
      resType: 'arraybuffer',
    };
    const res = await sendRequestWithQSParamsSerializer('get', endpoint, options);

    const { data, status } = res;

    if (R.and(isNotNilAndNotEmpty(data), isResponseSuccess(status))) {
      openFileInWindowFromArrayBufferResponse(res);
    }
  } catch (error) {
    handleException('error', 'asyncGetFileAndOpenInWindow exception');
  }
};

const asyncGetLocationTemplate = async (guid) => {
  try {
    const res = await sendRequestWithQSParamsSerializer('get', endpointsMap.getCurrentLocationEndpoint(guid));

    const { data, status } = res;

    if (R.and(isNotNilAndNotEmpty(data), isResponseSuccess(status))) {
      return data;
    }
  } catch (error) {
    handleException('error', 'asyncGetLocationTemplate exception');
  }
};

const asyncGetReferenceDuplicates = async ({ references, branchGuid }) => {
  const options = {
    data: {
      references,
      [GC.BRANCH_GUID]: branchGuid,
    },
  };

  const res = await sendRequest('post', endpointsMap.cloReferenceCheckDuplicates, options);

  const { data, status } = res;

  if (isResponseSuccess(status)) {
    return data;
  }

  handleException('error', 'getReferenceDuplicates exception');

  return [];
};

const asyncGetTelsByCarrierInvoiceNumber = async ({ invoiceGuid, carrierGuid, invoiceNumber } = {}) => {
  try {
    if (R.any(isNilOrEmpty, [carrierGuid, invoiceNumber])) return [];

    const options = {
      data: {
        invoiceGuid,
        carrierGuid,
        invoiceNumber,
      },
    };

    const { data, status } = await sendRequest(
      'post',
      endpointsMap.carrierInvoiceGetTelsByInvoiceNumber,
      options,
    );

    if (isResponseSuccess(status)) {
      return R.or(data, []);
    }

    return null;
  } catch (error) {
    handleException('error', 'asyncGetTelsByCarrierInvoiceNumber exception');

    return null;
  }
};

export {
  asyncGetConfigsByNames,
  asyncGetVinLookupConfig,
  asyncGetAccessorialsList,
  asyncGetFileAndCreateSrc,
  asyncGetLocationTemplate,
  asyncGetFileAndOpenInWindow,
  asyncGetReferenceDuplicates,
  asyncGetTelsByCarrierInvoiceNumber,
};
